import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from "react-redux";
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { FaStarOfLife } from 'react-icons/fa';
import Loader from '../components/Loader';
import ReactQuill from 'react-quill';
import { createTestimonialAPI } from '../../services/api/Testimonial';

const AddTestimonial = () => {
    const user = useSelector((state) => state.LoginReducer.authData);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const validationSchema = Yup.object({
        name: Yup.string()
            .min(3, 'Name must be at least 3 characters long')
            .required('Name is required'),
        comment: Yup.string()
            .max(255, 'Comment must be less than or equal to 255 characters')
            .required('Comment is required'),
        image: Yup.mixed()
            .required('Image is required')
            .test('fileType', 'Only JPEG and PNG files are allowed', (value) => {
                return value && (value.type === 'image/jpeg' || value.type === 'image/png');
            })
            .test('fileSize', 'File size exceeds the 2MB limit', (value) => {
                return value && value.size <= 2 * 1024 * 1024; // 2MB limit
            }),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            comment: '',
            image: null,
            user_id: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('user_id', user.data.id ?? 1);
            formData.append('name', values.name);
            formData.append('comment', values.comment);
            formData.append('image', values.image);
            await createTestimonial(formData);
        },
    });

    // Handle image change
    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        formik.setFieldValue('image', file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                formik.setFieldValue('imagePreview', reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const createTestimonial = async (formData) => {
        try {
            const token = user.data.token;
            const data = await createTestimonialAPI(formData, token);
            setLoading(false);
            if (data.status) {
                Swal.fire({
                    title: 'Testimonial Created!',
                    text: 'The Testimonial has been created successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    navigate('/testimonials');
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    const modules = {
        toolbar: [
          [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          ['link', 'image'],
          [{ 'align': [] }],
          ['clean']
        ],
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Layout title="Create Testimonial">
            <div className="container table-container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="name">Testimonial Name <FaStarOfLife className='text-danger' /></label>
                                    <input type="text" className="form-control mt-1" name="name" id="name" placeholder="Enter Testimonial name" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.name} />
                                </div>
                                {formik.touched.name && formik.errors.name ? ( <label className="text-danger">{formik.errors.name}</label>) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="comment">Comment <FaStarOfLife className='text-danger' /></label>
                                    <textarea className="form-control mt-1 mb-5" id="comment" name="comment" value={formik.values.comment} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Start typing..." style={{ height: '150px', borderRadius: '8px' }} />
                                    {formik.touched.comment && formik.errors.comment ? ( <label className="text-danger">{formik.errors.comment}</label>) : null}
                                </div>
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="image">Testimonials Image <FaStarOfLife className='text-danger' /></label>
                                    <input type="file" className="form-control mt-1" name="image" id="image" accept="image/*" onChange={handleImageChange} />
                                </div>
                                {formik.touched.image && formik.errors.image ? ( <label className="text-danger">{formik.errors.image}</label>) : null}
                            </div>
                            {formik.values.imagePreview && (
                                <div className="col-md-6">
                                    <img className='image-preview' src={formik.values.imagePreview} alt="Image Preview" />
                                </div>
                            )}
                        </div>
                        <div className="row mt-3">
                            <div className="col-md-6 col-12 d-flex gap-3">
                                <Link to={'/testimonials'} type="submit" className="btn btn-dark cancel_btn">Cancel</Link>
                                <button type="submit" className="btn btn-primary submit_btn">Submit</button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    )
}

export default AddTestimonial;